import React from 'react';
import { 
  WorkOrderCreator, 
  ReceiptManager, 
  AppointmentCalendar, 
  ClientManager, 
  SupplierManager, 
  ProfileEditorModal, 
  QuoteBuilderModal, 
  CostCenterHybrid, 
  ManagementDashboardHybrid, 
  SignatureDocumentCenter, 
  PlanUpgradeModal,
  DirectBookingPage,
  MarketIntelligenceRadar,
  ExecutiveCockpitDashboard,
  ErpHubDashboard
} from '../index';
import SpecialistBottomNav, { SpecialistTab } from '../components/SpecialistBottomNav';
import SpecialistQuickActionSheet from '../components/SpecialistQuickActionSheet';
import { EnterpriseGuard } from '../enterprise/guards/EnterpriseGuard';
import { useErpPlaygroundState } from '../hooks/useErpPlaygroundState';
import { NicheKey, StorefrontProfile, StaffMember, Receipt, ClinicalPatient } from '../types';

// Code-Splitting: Carregamento sob demanda (Lazy) dos submódulos Enterprise
const InventoryHubManager = React.lazy(() => import('../enterprise/inventory/InventoryHubManager'));
const PatientManager = React.lazy(() => import('../enterprise/clinical/PatientManager'));
const SoapConsultationEditor = React.lazy(() => import('../enterprise/clinical/SoapConsultationEditor'));
const VaccineRetentionCenter = React.lazy(() => import('../enterprise/clinical/VaccineRetentionCenter'));
const ExamVaultUploader = React.lazy(() => import('../enterprise/clinical/ExamVaultUploader'));
const AccountantClosingHub = React.lazy(() => import('../enterprise/accounting/AccountantClosingHub').then(m => ({ default: m.AccountantClosingHub })));
const AccountantPortalView = React.lazy(() => import('../enterprise/accounting/AccountantPortalView').then(m => ({ default: m.AccountantPortalView })));

function EnterpriseLoadingFallback() {
  return (
    <div className="min-h-100 flex flex-col items-center justify-center p-8 space-y-3 font-sans">
      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      <span className="text-xs font-black uppercase tracking-wider text-zinc-500">
        Carregando Módulo Plano Empresa...
      </span>
    </div>
  );
}

const SALAO_PROFILE: StorefrontProfile = {
  id: 'store_salao',
  userId: 'demo_user',
  slug: 'studio-bella',
  displayName: 'Studio Bella • Salão & Estética',
  tagline: 'Manicure, Alongamento em Gel, Mechas, Escova e Estética Facial',
  bio: 'Salão especializado em beleza e bem-estar feminino com profissionais certificadas, autoclave hospitalar e atendimento com hora marcada.',
  yearsExperience: '8 anos',
  nicheBadges: ['Salão de Beleza', 'Alongamento em Gel', 'Mechas & Loiros', 'Design de Sobrancelhas'],
  whatsappNumber: '11999999999',
  instagramHandle: '@studiobellasp',
  facebookHandle: 'fb.com/studiobellasp',
  tiktokHandle: '@studiobellasp',
  email: 'contato@studiobella.com.br',
  addressDisplay: 'Rua Oscar Freire, 1050 - Jardins, São Paulo - SP',
  accentColor: '#0d9488',
  isPublished: true,
  viewCount: 4120,
  portfolioItems: []
};

const SALAO_STAFF: StaffMember[] = [
  { id: 'st_any', name: 'Qualquer Profissional', role: 'Primeiro horário livre', color: 'emerald', commissionPercent: 50, active: true },
  { id: 'st_1', name: 'Mariana Silva', role: 'Manicure & Alongamento em Gel', color: 'rose', commissionPercent: 50, active: true },
  { id: 'st_2', name: 'Carla Dias', role: 'Cabeleireira & Mechas', color: 'purple', commissionPercent: 60, active: true },
  { id: 'st_3', name: 'Letícia Souza', role: 'Esteticista & Sobrancelhas', color: 'amber', commissionPercent: 50, active: true }
];

export default function ErpPlaygroundPage() {
  const [demoReceipts, setDemoReceipts] = React.useState<Receipt[]>([
    {
      id: 'rec_demo_1',
      userId: 'demo_user',
      receiptNumber: 'REC-1082',
      totalServiceValue: 650,
      amountPaid: 650,
      remainingBalance: 0,
      paymentMethod: 'pix',
      receiptType: 'full',
      installmentNumber: 1,
      totalInstallments: 1,
      clientName: 'Fernanda Montenegro Silveira',
      clientDocument: '119.882.340-92',
      description: 'Manutenção de Mechas Loiro Pérola + Reconstrução Capilar com Ozônio',
      warrantyPeriod: 30,
      warrantyUnit: 'dias',
      warrantyTerms: 'Garantia de retoque e integridade da fibra capilar em 30 dias.',
      issuedAt: new Date(Date.now() - 86400000 * 2).toISOString()
    },
    {
      id: 'rec_demo_2',
      userId: 'demo_user',
      receiptNumber: 'REC-1081',
      totalServiceValue: 380,
      amountPaid: 190,
      remainingBalance: 190,
      paymentMethod: 'pix',
      receiptType: 'deposit',
      installmentNumber: 1,
      totalInstallments: 2,
      clientName: 'Juliana Paes Vasconcelos',
      clientDocument: '228.910.450-18',
      description: 'Sinal de 50% para Alongamento em Fibra de Vidro + Esmaltação em Gel',
      warrantyPeriod: 15,
      warrantyUnit: 'dias',
      warrantyTerms: 'Garantia contra descolamento ou quebra em até 15 dias.',
      issuedAt: new Date(Date.now() - 86400000 * 5).toISOString()
    }
  ]);
  const {
    currentView,
    setCurrentView,
    selectedNiche,
    setSelectedNiche,
    simulatedPlanTier,
    setSimulatedPlanTier,
    canAccess,
    handleActionClick,
    isUpgradeModalOpen,
    setIsUpgradeModalOpen,
    upgradeRequiredTier,
    setUpgradeRequiredTier,
    upgradeFeatureName,
    setUpgradeFeatureName,
    upgradeFeatureDescription,
    setUpgradeFeatureDescription,
    financialState,
    setFinancialState,
    calculatedVhbRate,
    clients,
    saveClient,
    updateClientVehicle,
    suppliers,
    saveSupplier,
    deleteSupplier,
    appointments,
    saveAppointment,
    updateAppointmentStatus,
    deductPackageSession,
    kanbanItems,
    moveKanbanStage,
    addKanbanQuote,
    demoProfile,
    setDemoProfile,
    handleBookFromChatbot,
    inventoryBatches,
    stockMovements,
    saveInventoryBatch,
    deleteInventoryBatch,
    saveStockMovement,
    clinicalPatients,
    saveClinicalPatient,
    deleteClinicalPatient,
    clinicalConsultations,
    saveClinicalConsultation,
    clinicalVaccinations,
    saveClinicalVaccination,
    deleteClinicalVaccination,
    clinicalExams,
    saveClinicalExam,
    deleteClinicalExam,
    executeConsultationCheckout,
    recordVaccineWhatsAppSent,
    accountantAccesses,
    inviteAccountant,
    revokeAccountant
  } = useErpPlaygroundState();

  const [activeConsultationPatient, setActiveConsultationPatient] = React.useState<ClinicalPatient | null>(() => {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('consultation') === 'true') {
          return {
            id: 'pat_demo_thor',
            userId: 'demo_user',
            clientId: 'c1',
            clientName: 'Beatriz Lima',
            clientPhone: '(11) 98765-4321',
            name: 'Thor',
            kind: 'pet',
            active: true,
            species: 'Canino',
            breed: 'Golden Retriever',
            gender: 'M',
            isNeutered: true,
            bloodType: 'DEA 1.1 Negativo',
            knownAllergies: 'Frango, Dipirona',
            chronicConditions: 'Dermatite Atópica',
            emergencyContact: '(11) 99999-8888',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
        }
      } catch {}
    }
    return null;
  });
  const [isAccountantPortalMode, setIsAccountantPortalMode] = React.useState(false);
  const [isQuickActionSheetOpen, setIsQuickActionSheetOpen] = React.useState(false);

  const activeBottomTab: SpecialistTab | null = 
    currentView === 'hub' ? 'home' :
    currentView === 'orcamento' ? 'orcamento' :
    currentView === 'agenda' ? 'agenda' :
    currentView === 'clientes' ? 'clientes' : null;

  const handleSelectBottomTab = (tab: SpecialistTab) => {
    if (tab === 'home') {
      if (currentView === 'hub') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setCurrentView('hub');
      }
    } else if (tab === 'orcamento') {
      setCurrentView('orcamento');
    } else if (tab === 'agenda') {
      setCurrentView('agenda');
    } else if (tab === 'clientes') {
      setCurrentView('clientes');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 font-sans">
      {/* Banner de Identificação de Laboratório / Sandbox */}
      <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-1.5 text-xs text-amber-900 flex items-center justify-between gap-2 z-50">
        <div className="flex items-center gap-2">
          <span className="font-black uppercase tracking-wider text-[10px] bg-amber-500 text-zinc-950 px-2 py-0.5 rounded font-mono">
            SANDBOX
          </span>
          <span className="font-medium text-amber-900 text-[11px]">
            Ambiente de Demonstração e Gravação de Tutoriais — Dados voláteis (em memória) para teste dos recursos.
          </span>
        </div>
        <a 
          href="/app" 
          className="text-[11px] font-bold text-amber-900 hover:text-black underline underline-offset-2 shrink-0"
        >
          Ir para o Sistema Real →
        </a>
      </div>

      {/* Barra de Simulação Comercial — ativa para alternar entre planos no Playground */}
      {currentView !== 'agendar' && (
        <div className="bg-neutral-950 text-white px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-900 text-xs sticky top-0 z-50 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest bg-amber-400/10 text-amber-300 px-2 py-0.5 rounded-md border border-amber-400/20">
              Simulador de Planos
            </span>
            <span className="text-zinc-400 font-medium hidden sm:inline text-xs">
              Alterne para simular a experiência por assinatura:
            </span>
          </div>

          <div className="flex bg-neutral-900 p-1 rounded-xl border border-neutral-800 gap-1">
            <button
              type="button"
              onClick={() => setSimulatedPlanTier('free')}
              className={`px-3 py-1 rounded-lg font-black text-xs transition-all cursor-pointer ${
                simulatedPlanTier === 'free' ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-400 hover:text-white'
              }`}
            >
              🆓 Grátis (5 orç/mês)
            </button>
            <button
              type="button"
              onClick={() => setSimulatedPlanTier('pro')}
              className={`px-3 py-1 rounded-lg font-black text-xs transition-all cursor-pointer ${
                simulatedPlanTier === 'pro' ? 'bg-brand text-white shadow-sm' : 'text-zinc-400 hover:text-white'
              }`}
            >
              ⚡ Plano PRO (R$ 19,90)
            </button>
            <button
              type="button"
              onClick={() => setSimulatedPlanTier('premium')}
              className={`px-3 py-1 rounded-lg font-black text-xs transition-all cursor-pointer ${
                simulatedPlanTier === 'premium' ? 'bg-amber-500 text-zinc-950 shadow-sm' : 'text-zinc-400 hover:text-white'
              }`}
            >
              👑 Especialista (R$ 49,90)
            </button>
            <button
              type="button"
              onClick={() => setSimulatedPlanTier('enterprise')}
              className={`px-3 py-1 rounded-lg font-black text-xs transition-all cursor-pointer ${
                simulatedPlanTier === 'enterprise' ? 'bg-indigo-600 text-white shadow-sm' : 'text-zinc-400 hover:text-white'
              }`}
            >
              🏢 Plano Empresa (R$ 99,00)
            </button>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* 1. TELA PRINCIPAL: HUB UNIFICADO COM ESCADA FREEMIUM                     */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {currentView === 'hub' && (
        <ErpHubDashboard
          demoProfile={demoProfile}
          simulatedPlanTier={simulatedPlanTier}
          calculatedVhbRate={calculatedVhbRate}
          canAccess={canAccess}
          handleActionClick={handleActionClick}
          onNavigate={setCurrentView}
          quotesCount={kanbanItems.length}
          maxFreeQuotes={5}
        />
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* 2. SUB-APLICAÇÕES                                                        */}
      {/* ──────────────────────────────────────────────────────────────────────── */}

      {/* Sub-Aplicação: Minha Agenda Online (Link Direto de Agendamento) */}
      {currentView === 'agendar' && (
        <DirectBookingPage
          profile={selectedNiche === 'estetica' ? SALAO_PROFILE : demoProfile}
          nicheKey={selectedNiche}
          staffList={selectedNiche === 'estetica' ? SALAO_STAFF : undefined}
          existingAppointments={appointments}
          showDemoSwitcher={true}
          onBookAppointment={async (newAppt) => {
            saveAppointment(newAppt);
          }}
          onBack={() => setCurrentView('hub')}
        />
      )}

      {/* Sub-Aplicação: Orçamentos (IA) */}
      {currentView === 'orcamento' && (
        <div className="min-h-screen bg-zinc-50">
          <QuoteBuilderModal
            vhbRate={calculatedVhbRate}
            defaultNiche={selectedNiche}
            onSaveQuote={async (q) => {
              addKanbanQuote(q);
              setCurrentView('hub');
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Centro de Custos Híbrido */}
      {currentView === 'custos' && (
        <div className="min-h-screen bg-zinc-50">
          <CostCenterHybrid
            financialState={financialState}
            onUpdateFinancialState={async (updated) => setFinancialState(updated)}
            onOpenSuppliers={() => setCurrentView('fornecedores')}
            onAutoSaveSupplier={(newSup) => saveSupplier(newSup)}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Ordem de Serviço (OS com Fotos & Zero Digitação) */}
      {currentView === 'os' && (
        <div className="min-h-screen bg-zinc-50">
          <WorkOrderCreator
            nicheKey={selectedNiche}
            providerName={demoProfile.displayName}
            onSave={async (wo) => {
              if (wo.vehicleData) {
                updateClientVehicle(wo.clientName, wo.vehicleData);
              }
              alert(`Ordem de Serviço #${wo.orderNumber} salva com sucesso!`);
              setCurrentView('hub');
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Fornecedores & Suprimentos */}
      {currentView === 'fornecedores' && (
        <div className="min-h-screen bg-zinc-50">
          <SupplierManager
            suppliers={suppliers}
            onSaveSupplier={async (s) => {
              saveSupplier(s);
              alert(`Fornecedor ${s.tradeName || s.name} salvo com sucesso!`);
            }}
            onDeleteSupplier={async (id) => {
              deleteSupplier(id);
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}




      {/* Sub-Aplicação: Recibos */}
      {currentView === 'recibo' && (
        <div className="min-h-screen bg-zinc-50">
          <ReceiptManager
            receipts={demoReceipts}
            user={{
              businessName: demoProfile.displayName,
              document: "12.345.678/0001-90",
              pixKey: "contato@fabioprestador.com.br",
              city: "São Paulo",
              state: "SP",
              phone: "11999998888",
              businessAddress: "Av. Paulista, 1000",
              showAddress: true
            }}
            providerName={demoProfile.displayName}
            providerDocument="CNPJ: 12.345.678/0001-90"
            providerPixKey="contato@fabioprestador.com.br"
            providerCity="São Paulo"
            onSaveReceipt={async (r) => {
              setDemoReceipts(prev => [r, ...prev.filter(x => x.id !== r.id)]);
            }}
            onDeleteReceipt={async (id) => {
              setDemoReceipts(prev => prev.filter(x => x.id !== id));
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Clientes & CRM */}
      {currentView === 'clientes' && (
        <div className="min-h-screen bg-zinc-50">
          <ClientManager
            clients={clients}
            onSaveClient={async (c) => {
              saveClient(c);
              alert(`Cliente ${c.name} salvo com sucesso no banco de dados!`);
            }}
            onCreateQuoteForClient={(c) => {
              setCurrentView('orcamento');
            }}
            onCreateWorkOrderForClient={(c) => {
              setCurrentView('os');
            }}
            onCreateAppointmentForClient={(c) => {
              setCurrentView('agenda');
            }}
            onCreateReceiptForClient={(c) => {
              setCurrentView('recibo');
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Agenda de Atendimentos */}
      {currentView === 'agenda' && (
        <div className="min-h-screen bg-zinc-50">
          <AppointmentCalendar
            providerName={demoProfile.displayName}
            slug={demoProfile.slug}
            appointments={appointments}
            onSaveAppointment={async (a) => {
              saveAppointment(a);
              alert('Novo agendamento salvo!');
            }}
            onUpdateStatus={async (id, status) => {
              updateAppointmentStatus(id, status);
            }}
            onDeductPackageSession={async (id) => {
              deductPackageSession(id);
              alert('Sessão baixada com sucesso!');
            }}
            onOpenReceipt={(appt) => {
              setCurrentView('recibo');
            }}
            onOpenDirectBooking={() => {
              setCurrentView('agendar');
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Gestão / Funil Kanban & Métricas */}
      {currentView === 'gestao' && (
        <div className="min-h-screen bg-zinc-50">
          <ManagementDashboardHybrid
            items={kanbanItems}
            onMoveStage={async (itemId, newStage) => {
              moveKanbanStage(itemId, newStage);
            }}
            onOpenItem={(item) => {
              setCurrentView('os');
            }}
            onCreateNewQuote={() => {
              setCurrentView('orcamento');
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Meu Cadastro */}
      {currentView === 'cadastro' && (
        <div className="min-h-screen bg-zinc-50">
          <ProfileEditorModal
            profile={demoProfile}
            financialState={financialState}
            onSaveProfile={async (p, f) => {
              setDemoProfile(p);
              setFinancialState(f);
            }}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Central de Assinaturas */}
      {currentView === 'assinatura' && (
        <div className="min-h-screen bg-zinc-50">
          <SignatureDocumentCenter
            providerName={demoProfile.displayName}
            clients={clients}
            onBack={() => setCurrentView('hub')}
          />
        </div>
      )}

      {/* Sub-Aplicação: Radar de Mercado & Benchmarking */}
      {currentView === 'radar' && (
        <div className="min-h-screen bg-zinc-50">
          <MarketIntelligenceRadar
            userCity={demoProfile.addressDisplay?.split(',')[1]?.split('-')[0]?.trim() || 'São Paulo'}
            userState={demoProfile.addressDisplay?.split('-')[1]?.trim() || 'SP'}
            userNiche={demoProfile.nicheBadges?.[0] || 'Serviços Técnicos e Especializados'}
            currentVhbRate={calculatedVhbRate}
            monthlyBillableHours={financialState.workHoursPerDay * financialState.workDaysPerWeek * 4}
            onBack={() => setCurrentView('hub')}
            onApplyNewVhbRate={(newVhb) => {
              alert(`Valor hora base atualizado para ${newVhb.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/h com sucesso!`);
            }}
          />
        </div>
      )}

      {/* Sub-Aplicação: Cockpit Executivo & DRE */}
      {currentView === 'cockpit' && (
        <div className="min-h-screen bg-zinc-50">
          <ExecutiveCockpitDashboard
            monthlyFixedCosts={financialState.fixedCosts.reduce((sum, c) => sum + c.monthlyValue, 0)}
            proLaboreGoal={financialState.desiredProLabore}
            currentVhbRate={calculatedVhbRate}
            monthlyCapacityHours={financialState.workHoursPerDay * financialState.workDaysPerWeek * 4}
            proposals={kanbanItems.map(k => ({
              id: k.id,
              title: k.title,
              clientName: k.clientName,
              clientPhone: '11999999999',
              rawServiceName: k.title,
              baseHourRate: calculatedVhbRate,
              totalLaborHours: 3.5,
              materialsCost: Math.round(k.finalPrice * 0.25),
              suggestedPrice: k.finalPrice,
              finalPrice: k.finalPrice,
              stage: k.stage as any,
              createdAt: 'Hoje',
              updatedAt: k.updatedAt
            }))}
            onBack={() => setCurrentView('hub')}
            onNavigateToCostCenter={() => setCurrentView('custos')}
            onNavigateToQuotes={() => setCurrentView('orcamento')}
            onNavigateToCalendar={() => setCurrentView('agenda')}
          />
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* 4. SUB-APLICAÇÕES ENTERPRISE (ESTOQUE, PRONTUÁRIO, VACINAS, EXAMES)      */}
      {/* ──────────────────────────────────────────────────────────────────────── */}

      {/* Sub-Aplicação Enterprise: Estoque & Lotes (100% Tabelas Financeiras) */}
      {currentView === 'estoque_hub' && (
        <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <EnterpriseGuard
            currentTier={simulatedPlanTier}
            featureName="Gestão de Estoque & Lotes"
            featureDescription="Controle rigoroso de validades, lotes e extrato de movimentos estilo conta corrente."
            onUpgrade={() => {
              setUpgradeRequiredTier('enterprise');
              setUpgradeFeatureName('Gestão de Estoque & Lotes');
              setUpgradeFeatureDescription('Balancete financeiro, lotes por validade e extrato de movimentos.');
              setIsUpgradeModalOpen(true);
            }}
            onBack={() => setCurrentView('hub')}
          >
            <React.Suspense fallback={<EnterpriseLoadingFallback />}>
              <InventoryHubManager
                batches={inventoryBatches}
                movements={stockMovements}
                suppliers={suppliers}
                onSaveBatch={saveInventoryBatch}
                onDeleteBatch={deleteInventoryBatch}
                onSaveMovement={saveStockMovement}
                onBack={() => setCurrentView('hub')}
              />
            </React.Suspense>
          </EnterpriseGuard>
        </div>
      )}

      {/* Sub-Aplicação Enterprise: Prontuário SOAP & CRM de Pacientes */}
      {currentView === 'prontuario_soap' && (
        <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <EnterpriseGuard
            currentTier={simulatedPlanTier}
            featureName="Prontuário Eletrônico SOAP Legal"
            featureDescription="Anamnese, sinais vitais, hipótese diagnóstica e conduta com imutabilidade jurídica e receita em PDF."
            onUpgrade={() => {
              setUpgradeRequiredTier('enterprise');
              setUpgradeFeatureName('Prontuário SOAP & CRM de Pacientes');
              setUpgradeFeatureDescription('Ficha médica imutável, receituário e checkout integrado.');
              setIsUpgradeModalOpen(true);
            }}
            onBack={() => setCurrentView('hub')}
          >
            <React.Suspense fallback={<EnterpriseLoadingFallback />}>
              {activeConsultationPatient ? (
                <SoapConsultationEditor
                  patient={activeConsultationPatient}
                  existingConsultation={clinicalConsultations.find(c => c.patientId === activeConsultationPatient.id && c.status === 'in_progress') || null}
                  availableBatches={inventoryBatches}
                  onSaveConsultation={saveClinicalConsultation}
                  onExecuteCheckout={executeConsultationCheckout}
                  onBack={() => setActiveConsultationPatient(null)}
                />
              ) : (
                <PatientManager
                  patients={clinicalPatients}
                  clients={clients}
                  consultations={clinicalConsultations}
                  vaccinations={clinicalVaccinations}
                  exams={clinicalExams}
                  onSavePatient={saveClinicalPatient}
                  onDeletePatient={deleteClinicalPatient}
                  onStartConsultation={(pat) => setActiveConsultationPatient(pat)}
                  onBack={() => setCurrentView('hub')}
                />
              )}
            </React.Suspense>
          </EnterpriseGuard>
        </div>
      )}

      {/* Sub-Aplicação Enterprise: Central de Retorno de Vacinas 1-Click */}
      {currentView === 'retorno_vacinas' && (
        <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <EnterpriseGuard
            currentTier={simulatedPlanTier}
            featureName="Central de Retorno de Vacinas no WhatsApp"
            featureDescription="Disparo nativo via Deep Link com pré-filtro da agenda online e agrupamento combo de múltiplos pets do mesmo tutor."
            onUpgrade={() => {
              setUpgradeRequiredTier('enterprise');
              setUpgradeFeatureName('Central de Retorno de Vacinas');
              setUpgradeFeatureDescription('Lembretes inteligentes via WhatsApp nativo sem custos de API.');
              setIsUpgradeModalOpen(true);
            }}
            onBack={() => setCurrentView('hub')}
          >
            <React.Suspense fallback={<EnterpriseLoadingFallback />}>
              <VaccineRetentionCenter
                vaccinations={clinicalVaccinations}
                patients={clinicalPatients}
                clients={clients}
                storeSlug={demoProfile.slug || 'clinica-saude'}
                storeName={demoProfile.displayName || 'R-MAX Clínica & Pet'}
                onSaveVaccination={saveClinicalVaccination}
                onDeleteVaccination={deleteClinicalVaccination}
                onRecordWhatsAppSent={recordVaccineWhatsAppSent}
                onBack={() => setCurrentView('hub')}
              />
            </React.Suspense>
          </EnterpriseGuard>
        </div>
      )}

      {/* Sub-Aplicação Enterprise: Cofre de Exames & DICOM R2 */}
      {currentView === 'cofre_exames' && (
        <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <EnterpriseGuard
            currentTier={simulatedPlanTier}
            featureName="Cofre de Exames & Imagens DICOM"
            featureDescription="Guarda legal de 5 anos (CFMV), compressão client-side WebP e visualizador com janela óssea."
            onUpgrade={() => {
              setUpgradeRequiredTier('enterprise');
              setUpgradeFeatureName('Cofre de Exames & DICOM R2');
              setUpgradeFeatureDescription('Arquivamento em nuvem com conformidade normativa e régua milimétrica.');
              setIsUpgradeModalOpen(true);
            }}
            onBack={() => setCurrentView('hub')}
          >
            <React.Suspense fallback={<EnterpriseLoadingFallback />}>
              <ExamVaultUploader
                exams={clinicalExams}
                patients={clinicalPatients}
                onSaveExam={saveClinicalExam}
                onDeleteExam={deleteClinicalExam}
                onBack={() => setCurrentView('hub')}
              />
            </React.Suspense>
          </EnterpriseGuard>
        </div>
      )}

      {/* Sub-Aplicação Enterprise: Portal do Contador & Fechamento Fiscal (Forma B) */}
      {currentView === 'portal_contador' && (
        <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <EnterpriseGuard
            currentTier={simulatedPlanTier}
            featureName="Portal do Contador Dedicado (Forma B)"
            featureDescription="Acesso exclusivo para auditoria fiscal com compliance LGPD e exportação para Domínio e Alterdata."
            onUpgrade={() => {
              setUpgradeRequiredTier('enterprise');
              setUpgradeFeatureName('Portal do Contador');
              setUpgradeFeatureDescription('Acesso externo para contabilidade com total isolamento de prontuários.');
              setIsUpgradeModalOpen(true);
            }}
            onBack={() => setCurrentView('hub')}
          >
            <React.Suspense fallback={<EnterpriseLoadingFallback />}>
              {isAccountantPortalMode ? (
                <AccountantPortalView
                  receipts={demoReceipts}
                  stockMovements={stockMovements}
                  consultations={clinicalConsultations}
                  clinicName={demoProfile.displayName || 'R-MAX Clínica & Saúde'}
                  onBackToClinic={() => setIsAccountantPortalMode(false)}
                />
              ) : (
                <AccountantClosingHub
                  receipts={demoReceipts}
                  stockMovements={stockMovements}
                  consultations={clinicalConsultations}
                  accountantAccesses={accountantAccesses}
                  clinicName={demoProfile.displayName || 'R-MAX Clínica & Saúde'}
                  onInviteAccountant={inviteAccountant}
                  onRevokeAccountant={revokeAccountant}
                  onSwitchToAccountantView={() => setIsAccountantPortalMode(true)}
                />
              )}
            </React.Suspense>
          </EnterpriseGuard>
        </div>
      )}

      {/* Modal de Upgrade de Planos */}
      <PlanUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        requiredTier={upgradeRequiredTier}
        currentTier={simulatedPlanTier}
        featureName={upgradeFeatureName}
        featureDescription={upgradeFeatureDescription}
        onSelectPlan={(tier) => {
          setSimulatedPlanTier(tier);
          if (tier === 'enterprise') {
            alert('Plano Empresa ativado! Módulos de Estoque por Lotes, Prontuário SOAP Legal, Central de Retorno de Vacinas e Cofre DICOM desbloqueados com sucesso.');
          } else if (simulatedPlanTier === 'pro' && tier === 'premium') {
            alert('Upgrade ativado! Aproveite os dias restantes do seu ciclo. A cobrança do Especialista Premium iniciará apenas no próximo mês.');
          } else if (tier === 'premium') {
            alert('Plano Especialista Premium ativado! Recursos de gestão e ERP desbloqueados com sucesso.');
          } else {
            alert(`Plano atualizado para: ${tier.toUpperCase()}! Recursos desbloqueados com sucesso.`);
          }
        }}
      />

      {/* Menu Inferior M3 Fixo e Persistente do Especialista (em todas as telas exceto agendamento público do cliente) */}
      {currentView !== 'agendar' && (
        <>
          <SpecialistBottomNav
            activeTab={activeBottomTab}
            onSelectTab={handleSelectBottomTab}
            onOpenQuickActions={() => setIsQuickActionSheetOpen(true)}
          />

          <SpecialistQuickActionSheet
            isOpen={isQuickActionSheetOpen}
            onClose={() => setIsQuickActionSheetOpen(false)}
            onNavigate={(target) => {
              setCurrentView(target);
              setIsQuickActionSheetOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}
